import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useState } from 'react';
import api, { BACKEND_URL } from '../api/axios';
import AttendanceControls from '../components/AttendanceControls';
import WhatsappMenu from '../components/WhatsappMenu';


const NAV_ITEMS = [
  { label: 'Dashboard', path: '/agent/dashboard', icon: '🏠' },
  { label: 'Candidates', path: '/agent/candidates', icon: '👤' },
  { label: 'Employers', path: '/agent/employers', icon: '🏢' },
  { label: 'Jobs', path: '/agent/jobs', icon: '💼' },
  // { label: 'Job Applications', path: '/agent/aggregated-applications', icon: '📇' },
  { label: 'Follow-ups', path: '/agent/follow-ups', icon: '📞' },
  { label: 'Applications', path: '/agent/interviews', icon: '🗓️' },
  { label: 'Verification', path: '/agent/verification', icon: '🛡️' },
   { label: 'Businesses', path: '/agent/businesses', icon: '🏬' },
   { label: 'Subscriptions', path: '/agent/subscriptions', icon: '💳' },
   { label: 'Renewals', path: '/agent/expiring-plans', icon: '⏳' },
   { label: 'Support Tickets', path: '/agent/support-tickets', icon: '🎫' },
  { label: 'Reports', path: '/agent/reports', icon: '📊' },
  { label: 'Settings', path: '/agent/settings', icon: '⚙️' },
];

// Wraps every /agent/* page. Keeps the sidebar/topbar identical across
// pages so Step 2's layout doesn't need to be rebuilt in every later step —
// each new page just drops its content inside <AgentLayout>...</AgentLayout>.
function AgentLayout({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const [user] = useState(() => {
    const stored = localStorage.getItem('user');
    return stored ? JSON.parse(stored) : null;
  });

  const handleLogout = async () => {
    try {
      await api.post('/logout');
    } catch (err) {
      console.error('Logout request failed', err);
    } finally {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      navigate('/login');
    }
  };

  const navLink = (item, onClick) => {
    const active = location.pathname === item.path;
    return (
      <Link
        key={item.path}
        to={item.path}
        onClick={onClick}
        className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
          active
            ? 'bg-gradient-to-r from-[#7C3AED] to-[#DB2777] text-white shadow-sm'
            : 'text-gray-600 hover:bg-gray-50'
        }`}
      >
        <span>{item.icon}</span>
        {item.label}
      </Link>
    );
  };

  return (
    <div className="min-h-screen bg-[#F5EEFB] flex flex-col">
      {/* Top Navigation */}
      <header className="bg-gradient-to-r from-[#7C3AED] to-[#DB2777] shadow-sm sticky top-0 z-20">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-3">
            <button
              className="lg:hidden text-white text-xl px-1"
              onClick={() => setSidebarOpen((v) => !v)}
              aria-label="Toggle menu"
            >
              ☰
            </button>
            <span className="font-bold text-lg text-white">
              PlanB <span className="text-pink-100">Agent</span>
            </span>
          </div>

         <div className="flex items-center gap-3">
  <WhatsappMenu base="/agent/whatsapp-messages" />
  <AttendanceControls />
  <div className="w-px h-6 bg-white/25 hidden sm:block" />
  <span className="text-sm text-white/90 hidden sm:inline">{user?.name}</span>
  <div className="w-9 h-9 rounded-full bg-white/15 text-white flex items-center justify-center font-bold text-sm ring-1 ring-white/30">
    {user?.name ? user.name.charAt(0).toUpperCase() : 'A'}
  </div>
  <button
    onClick={handleLogout}
    className="text-sm font-semibold text-white border border-white/40 px-3 py-1.5 rounded-lg hover:bg-white/10 transition-colors"
  >
    Logout
  </button>
</div>
        </div>
      </header>

      <div className="flex flex-1">
        {/* Sidebar - desktop */}
        <aside className="hidden lg:block w-56 bg-white border-r border-gray-100 shrink-0 sticky top-[57px] h-[calc(100vh-57px)] overflow-y-auto pb-6">
          <div className="p-3">
            <div className="bg-gradient-to-r from-[#7C3AED] to-[#DB2777] rounded-xl px-3 py-3 flex items-center gap-3 mb-3">
              <div className="w-9 h-9 rounded-full bg-white/20 text-white flex items-center justify-center font-bold text-sm shrink-0">
                {user?.name ? user.name.charAt(0).toUpperCase() : 'A'}
              </div>
              <div className="min-w-0">
                <p className="text-white text-sm font-semibold truncate">{user?.name || 'Agent'}</p>
                <p className="text-pink-100 text-xs uppercase tracking-wide">Agent</p>
              </div>
            </div>
          </div>
          <nav className="px-3 pb-3 space-y-1">
            {NAV_ITEMS.map((item) => navLink(item))}
          </nav>
        </aside>

        {/* Sidebar - mobile drawer */}
        {sidebarOpen && (
          <>
            <div
              className="fixed inset-0 bg-black/30 z-30 lg:hidden"
              onClick={() => setSidebarOpen(false)}
            />
            <aside className="fixed top-0 left-0 h-full w-64 bg-white z-40 shadow-xl lg:hidden overflow-y-auto pb-6">
              <div className="p-4 border-b border-gray-100 flex items-center justify-between">
                <span className="font-bold text-[#0D1B3D]">Menu</span>
                <button onClick={() => setSidebarOpen(false)} className="text-gray-500 text-lg">✕</button>
              </div>
              <div className="p-3">
                <div className="bg-gradient-to-r from-[#7C3AED] to-[#DB2777] rounded-xl px-3 py-3 flex items-center gap-3 mb-3">
                  <div className="w-9 h-9 rounded-full bg-white/20 text-white flex items-center justify-center font-bold text-sm shrink-0">
                    {user?.name ? user.name.charAt(0).toUpperCase() : 'A'}
                  </div>
                  <div className="min-w-0">
                    <p className="text-white text-sm font-semibold truncate">{user?.name || 'Agent'}</p>
                    <p className="text-pink-100 text-xs uppercase tracking-wide">Agent</p>
                  </div>
                </div>
              </div>
              <nav className="px-3 pb-3 space-y-1">
                {NAV_ITEMS.map((item) => navLink(item, () => setSidebarOpen(false)))}
              </nav>
            </aside>
          </>
        )}

        {/* Page content */}
        <main className="flex-1 min-w-0 p-4 sm:p-6">{children}</main>
      </div>
    </div>
  );
}

export default AgentLayout;